import { apiClient } from './apiClient';
import type { TechnologyCatalogItemRef, TechnologyListResult } from './technologyApi';

export type TechnologyReleaseStatus =
  | 'Draft'
  | 'Planned'
  | 'Building'
  | 'Ready'
  | 'Released'
  | 'Cancelled';

export type TechnologyDeploymentStatus =
  | 'Scheduled'
  | 'InProgress'
  | 'Succeeded'
  | 'Failed'
  | 'Cancelled'
  | 'RolledBack';

export type TechnologyReleaseItemType = 'WorkItem' | 'PullRequest' | 'Note';

export type TechnologyReleaseItem = {
  id: string;
  applicationId?: string | null;
  applicationName?: string | null;
  itemType: TechnologyReleaseItemType | string;
  externalId?: string | null;
  title: string;
  url?: string | null;
  stateSnapshot?: string | null;
  includeInCommunications: boolean;
  sortOrder: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyReleaseApplication = {
  applicationId: string;
  applicationName?: string | null;
  componentVersion?: string | null;
  sortOrder: number;
};

export type TechnologyRelease = {
  id: string;
  version: string;
  title: string;
  status: TechnologyReleaseStatus | string;
  summaryHtml?: string | null;
  plannedAtUtc?: string | null;
  releasedAtUtc?: string | null;
  externalBuildId?: string | null;
  externalBuildUrl?: string | null;
  notes?: string | null;
  applications: TechnologyReleaseApplication[];
  items: TechnologyReleaseItem[];
  deploymentCount: number;
  createdByUserId: string;
  createdByUserName?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyReleaseApplicationUpsertPayload = {
  applicationId: string;
  componentVersion?: string | null;
  sortOrder?: number;
};

export type TechnologyReleaseUpsertPayload = {
  applications: TechnologyReleaseApplicationUpsertPayload[];
  version: string;
  title: string;
  status?: string | null;
  summaryHtml?: string | null;
  plannedAtUtc?: string | null;
  externalBuildId?: string | null;
  externalBuildUrl?: string | null;
  notes?: string | null;
};

export type TechnologyReleaseItemUpsertPayload = {
  applicationId?: string | null;
  itemType: string;
  externalId?: string | null;
  title: string;
  url?: string | null;
  stateSnapshot?: string | null;
  includeInCommunications?: boolean;
  sortOrder?: number;
};

export type TechnologyDeployment = {
  id: string;
  releaseId: string;
  releaseVersion?: string | null;
  releaseTitle?: string | null;
  applicationId?: string | null;
  applicationName?: string | null;
  environmentKind: string;
  status: TechnologyDeploymentStatus | string;
  scheduledAtUtc: string;
  startedAtUtc?: string | null;
  executedAtUtc?: string | null;
  finishedAtUtc?: string | null;
  approvedByUserId?: string | null;
  approvedByUserName?: string | null;
  resultNotes?: string | null;
  rollbackOfDeploymentId?: string | null;
  externalPipelineRunId?: string | null;
  externalPipelineRunUrl?: string | null;
  releaseItems: TechnologyReleaseItem[];
  createdByUserId: string;
  createdByUserName?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyDeploymentSchedulePayload = {
  releaseId: string;
  environmentKind: string;
  scheduledAtUtc: string;
  approvedByUserId?: string | null;
  resultNotes?: string | null;
};

export type TechnologyDeploymentReschedulePayload = {
  scheduledAtUtc: string;
  environmentKind?: string | null;
  approvedByUserId?: string | null;
  resultNotes?: string | null;
};

export type TechnologyDeploymentExecutionPayload = {
  resultNotes?: string | null;
  externalPipelineRunId?: string | null;
  externalPipelineRunUrl?: string | null;
  approvedByUserId?: string | null;
  executedAtUtc?: string | null;
};

export type TechnologyProductApplicationLink = {
  id: string;
  salesProductId: string;
  salesProductName?: string | null;
  applicationId: string;
  applicationName?: string | null;
  applicationNotes?: string | null;
  catalogItems?: TechnologyCatalogItemRef[];
  createdByUserId: string;
  createdAtUtc: string;
};

export async function listTechnologyReleases(params: {
  applicationId?: string;
  salesProductId?: string;
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologyRelease>> {
  const response = await apiClient.get<TechnologyListResult<TechnologyRelease>>(
    '/v1/technology/releases',
    { params },
  );
  return response.data;
}

export async function getTechnologyRelease(id: string): Promise<TechnologyRelease> {
  const response = await apiClient.get<TechnologyRelease>(`/v1/technology/releases/${id}`);
  return response.data;
}

export async function createTechnologyRelease(
  payload: TechnologyReleaseUpsertPayload,
): Promise<TechnologyRelease> {
  const response = await apiClient.post<TechnologyRelease>('/v1/technology/releases', payload);
  return response.data;
}

export async function updateTechnologyRelease(
  id: string,
  payload: TechnologyReleaseUpsertPayload,
): Promise<TechnologyRelease> {
  const response = await apiClient.put<TechnologyRelease>(`/v1/technology/releases/${id}`, payload);
  return response.data;
}

export async function deleteTechnologyRelease(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/releases/${id}`);
}

export async function addTechnologyReleaseItem(
  releaseId: string,
  payload: TechnologyReleaseItemUpsertPayload,
): Promise<TechnologyReleaseItem> {
  const response = await apiClient.post<TechnologyReleaseItem>(
    `/v1/technology/releases/${releaseId}/items`,
    payload,
  );
  return response.data;
}

export type TechnologyAzureImportPayload = {
  applicationId: string;
  workItemIds?: number[];
  pullRequestIds?: number[];
};

export type TechnologyAzureImportResult = {
  importedCount: number;
  skippedCount: number;
  items: TechnologyReleaseItem[];
  errors: string[];
};

export type TechnologyAzureRefreshResult = {
  updatedCount: number;
  importedCount: number;
  failedCount: number;
  items: TechnologyReleaseItem[];
  errors: string[];
};

export async function importTechnologyReleaseFromAzure(
  releaseId: string,
  payload: TechnologyAzureImportPayload,
): Promise<TechnologyAzureImportResult> {
  const response = await apiClient.post<TechnologyAzureImportResult>(
    `/v1/technology/releases/${releaseId}/azure/import`,
    payload,
  );
  return response.data;
}

export async function refreshTechnologyReleaseAzureItems(
  releaseId: string,
): Promise<TechnologyAzureRefreshResult> {
  const response = await apiClient.post<TechnologyAzureRefreshResult>(
    `/v1/technology/releases/${releaseId}/azure/refresh`,
  );
  return response.data;
}

export async function deleteTechnologyReleaseItem(releaseId: string, itemId: string): Promise<void> {
  await apiClient.delete(`/v1/technology/releases/${releaseId}/items/${itemId}`);
}

export async function listTechnologyDeployments(params: {
  releaseId?: string;
  applicationId?: string;
  environmentKind?: string;
  salesProductId?: string;
  status?: string;
  scheduledFromUtc?: string;
  scheduledToUtc?: string;
  upcomingOnly?: boolean;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologyDeployment>> {
  const response = await apiClient.get<TechnologyListResult<TechnologyDeployment>>(
    '/v1/technology/deployments',
    { params },
  );
  return response.data;
}

export async function getTechnologyDeployment(id: string): Promise<TechnologyDeployment> {
  const response = await apiClient.get<TechnologyDeployment>(`/v1/technology/deployments/${id}`);
  return response.data;
}

export async function scheduleTechnologyDeployment(
  payload: TechnologyDeploymentSchedulePayload,
): Promise<TechnologyDeployment> {
  const response = await apiClient.post<TechnologyDeployment>('/v1/technology/deployments', payload);
  return response.data;
}

export async function rescheduleTechnologyDeployment(
  id: string,
  payload: TechnologyDeploymentReschedulePayload,
): Promise<TechnologyDeployment> {
  const response = await apiClient.put<TechnologyDeployment>(
    `/v1/technology/deployments/${id}`,
    payload,
  );
  return response.data;
}

export async function startTechnologyDeployment(
  id: string,
  payload?: TechnologyDeploymentExecutionPayload,
): Promise<TechnologyDeployment> {
  const response = await apiClient.post<TechnologyDeployment>(
    `/v1/technology/deployments/${id}/start`,
    payload ?? {},
  );
  return response.data;
}

export async function completeTechnologyDeployment(
  id: string,
  payload?: TechnologyDeploymentExecutionPayload,
): Promise<TechnologyDeployment> {
  const response = await apiClient.post<TechnologyDeployment>(
    `/v1/technology/deployments/${id}/complete`,
    payload ?? {},
  );
  return response.data;
}

export async function failTechnologyDeployment(
  id: string,
  payload?: TechnologyDeploymentExecutionPayload,
): Promise<TechnologyDeployment> {
  const response = await apiClient.post<TechnologyDeployment>(
    `/v1/technology/deployments/${id}/fail`,
    payload ?? {},
  );
  return response.data;
}

export async function cancelTechnologyDeployment(
  id: string,
  payload?: TechnologyDeploymentExecutionPayload,
): Promise<TechnologyDeployment> {
  const response = await apiClient.post<TechnologyDeployment>(
    `/v1/technology/deployments/${id}/cancel`,
    payload ?? {},
  );
  return response.data;
}

export async function reopenTechnologyDeployment(
  id: string,
  payload: { comment: string },
): Promise<TechnologyDeployment> {
  const response = await apiClient.post<TechnologyDeployment>(
    `/v1/technology/deployments/${id}/reopen`,
    payload,
  );
  return response.data;
}

export async function rollbackTechnologyDeployment(
  id: string,
  payload?: TechnologyDeploymentExecutionPayload,
): Promise<TechnologyDeployment> {
  const response = await apiClient.post<TechnologyDeployment>(
    `/v1/technology/deployments/${id}/rollback`,
    payload ?? {},
  );
  return response.data;
}

export async function deleteTechnologyDeployment(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/deployments/${id}`);
}

export async function listTechnologyProductApplicationLinks(params?: {
  salesProductId?: string;
  applicationId?: string;
}): Promise<TechnologyProductApplicationLink[]> {
  const response = await apiClient.get<TechnologyProductApplicationLink[]>(
    '/v1/technology/product-application-links',
    { params },
  );
  return response.data;
}

export async function createTechnologyProductApplicationLink(payload: {
  salesProductId: string;
  applicationId: string;
}): Promise<TechnologyProductApplicationLink> {
  const response = await apiClient.post<TechnologyProductApplicationLink>(
    '/v1/technology/product-application-links',
    payload,
  );
  return response.data;
}

export async function deleteTechnologyProductApplicationLink(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/product-application-links/${id}`);
}
